import { liveReload } from '@shared/services/realtime.service';
import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { SelectionBarComponent } from '@app/shared/components/selection-bar/selection-bar.component';
import { CatalogImageService } from '@app/shared/services/catalog-image.service';
import { SelectionModel } from '@angular/cdk/collections';
import { SimpleExcelService } from '@app/shared/services/simple-excel.service';
import { FilterChipsComponent, FilterChip } from '@app/shared/components/filter-bar/filter-chips.component';
import { SearchPillComponent } from '@app/shared/components/filter-bar/search-pill.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { ModulesService } from '@shared/services/modules.service';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';

import { MaterialModule } from '@app/material.module';
import {
  InventoryToolbarComponent,
  InventoryKpi,
  InventoryScope,
} from '@app/shared/components/inventory-toolbar/inventory-toolbar.component';

import { StockInstrumentalsService } from '@shared/services/entities/stockInstrumentals.service';
import { InstrumentalsService } from '@shared/services/entities/instrumentals.service';
import { WarehousesService } from '@shared/services/entities/warehouse.service';
import { PlacesService } from '@shared/services/entities/places.service';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';

/**
 * Inventario de instrumental.
 *
 * Mismo patrón que Inventario de equipo y de productos:
 *
 *   · No se pide NADA al API hasta que haya sitio Y almacén. El inventario
 *     completo de todos los almacenes es una consulta cara y una tabla que
 *     mezcla existencias de sitios distintos sin avisar.
 *   · Los indicadores son botones: filtran la tabla y las tarjetas.
 *   · La vista (tabla o tarjetas) se recuerda por módulo.
 *
 * A diferencia de los otros dos, aquí el selector de sitio/almacén es el que
 * trae `app-inventory-toolbar`, porque esta pantalla se construyó de cero y
 * no arrastra un formulario previo.
 *
 * El vocabulario de estados es el mismo que el de equipo
 * (`Activo` / `Mantenimiento` / `Baja`) para que las dos pantallas se
 * comporten igual; la tarjeta del primero se llama "Disponibles", que es lo
 * que se entiende en instrumental.
 */
@Component({
  selector: 'app-inventory-instrumentals',
  standalone: true,
  imports: [PageLoaderComponent, SelectionBarComponent, SearchPillComponent, FilterChipsComponent, PageTitleComponent, 
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    InventoryToolbarComponent,
  ],
  templateUrl: './inventoryInstrumentals.component.html',
  styleUrls: ['./inventoryInstrumentals.component.scss'],
})
export class InventoryInstrumentalsComponent implements OnInit {
  private modulesSvcTitle = inject(ModulesService);
  /** Título del módulo = etiqueta visible del catálogo de Módulos. */
  title = this.modulesSvcTitle.moduleTitle('/inventoryInstrumentals', 'Inventario de instrumental');


  private readonly stockService = inject(StockInstrumentalsService);
  private readonly instrumentalsService = inject(InstrumentalsService);
  private readonly warehousesService = inject(WarehousesService);
  private readonly placesService = inject(PlacesService);
  private readonly customMessageService = inject(CustomMessageService);
  private readonly catalogImages = inject(CatalogImageService);
  private readonly simpleExcel = inject(SimpleExcelService);

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  // ─── Catálogos ────────────────────────────────────────────────────────
  placeList: any[] = [];
  warehouseList: any[] = [];
  /** id del instrumental → su registro de catálogo, para resolver columnas. */
  instrumentalObj: Record<string, any> = {};

  // ─── Alcance ──────────────────────────────────────────────────────────
  scope: InventoryScope = { place: '', warehouse: '' };
  get scopeReady(): boolean { return !!this.scope.place && !!this.scope.warehouse; }

  // ─── Vista e indicadores ──────────────────────────────────────────────
  viewMode: 'table' | 'cards' = InventoryToolbarComponent.readStoredView('instrumentals');
  activeKpi: string | null = null;
  kpis: InventoryKpi[] = [];

  /** Todo lo del almacén; `dataSource` muestra lo filtrado. */
  private allStock: any[] = [];

  displayedColumns = [
    'select', 'photo', 'instrumental', 'description', 'serialNumber', 'tagRfid',
    'manufacturerYear', 'numberUses', 'type', 'status',
  ];
  dataSource = new MatTableDataSource<any>([]);

  loading = false;

  /** Pantalla de carga del módulo: bloquea hasta terminar la carga inicial. */
  readonly pageLoad = new PageLoad();
  /** Tiempo real: recarga cuando otro usuario cambia estos módulos (WebSocket). */
  private readonly live = liveReload(['stockInstrumentals', 'instrumentals'], () => this.pageLoad.ready && this.pageLoad.refresh(this.search()));

  ngOnInit(): void {
    void this.init();
  }

  /** Carga inicial (también la usa "Reintentar"). */
  init(): Promise<boolean> {
    return this.pageLoad.run(() => this.initialLoad());
  }

  private async initialLoad(): Promise<void> {
    await this.loadCatalogues();
  }

  private async loadCatalogues(): Promise<void> {
    try {
      const [places, warehouses, instrumentals] = await Promise.all([
        this.placesService.search({ searchType: 'default', condition: { deleted: false } }),
        this.warehousesService.search({ searchType: 'default', condition: { deleted: false } }),
        this.instrumentalsService.search({ searchType: 'default', condition: { deleted: false } }),
      ]);
      this.placeList = (places as any)?.data ?? [];
      this.warehouseList = (warehouses as any)?.data ?? [];
      this.instrumentalObj = ((instrumentals as any)?.data ?? []).reduce(
        (acc: Record<string, any>, it: any) => { acc[it.id] = it; return acc; },
        {} as Record<string, any>);
    } catch {
      await this.customMessageService.open({
        title: 'Error', type: 'error',
        message: 'No se pudieron cargar los catálogos de ubicaciones y lockers.',
        dismissBtn: true,
      });
    }
  }

  // ─── Alcance ──────────────────────────────────────────────────────────
  async onScopeChange(scope: InventoryScope): Promise<void> {
    this.scope = scope;
    // Cambiar de alcance invalida el filtro: el KPI activo contaba sobre el
    // almacén anterior.
    this.activeKpi = null;
    await this.search();
  }

  async search(): Promise<void> {
    if (!this.scopeReady) {
      this.allStock = [];
      this.kpis = [];
      this.dataSource = new MatTableDataSource<any>([]);
      return;
    }

    this.loading = true;
    this.customMessageService.open({
      title: 'Espere', type: 'neutral', message: 'Cargando inventario...', loading: true,
    });

    try {
      const response: any = await this.stockService.search({
        searchType: 'default',
        condition: { deleted: false, warehouse: this.scope.warehouse },
      });
      const data: any[] = Array.isArray(response?.data) ? response.data : [];

      // Los campos legibles del catálogo se resuelven aquí, una vez. Si se
      // resolvieran en la plantilla con un getter, cada ciclo de detección de
      // cambios volvería a buscarlos por cada celda.
      this.allStock = data.map(r => {
        const cat = this.instrumentalObj[r.instrumental] ?? {};
        return {
          ...r,
          // El catálogo nuevo de Instrumental identifica por nombre (ya no hay código).
          _code: cat.name ?? cat.code ?? cat.model ?? 'N/A',
          _description: cat.description ?? 'N/A',
        };
      });

      this.selection.clear();
      this.buildKpis();
      this.applyKpiFilter();
      // Fotos del catálogo de Instrumental (se firman aparte y se repinta).
      void this.catalogImages.resolve(this.allStock, r => this.instrumentalObj[r.instrumental]?.image)
        .then(() => { this.dataSource.data = [...this.dataSource.data]; });
    } catch {
      await this.customMessageService.open({
        title: 'Error', type: 'error',
        message: 'No se pudo cargar el inventario de instrumental.',
        dismissBtn: true,
      });
    } finally {
      this.loading = false;
      this.customMessageService.close();
    }
  }

  // ─── Indicadores ──────────────────────────────────────────────────────
  /**
   * Cuentan sobre TODO el almacén, nunca sobre lo ya filtrado: si contaran
   * sobre lo filtrado, al prender un KPI los demás se irían a cero.
   */
  private buildKpis(): void {
    const lista = this.allStock ?? [];
    this.kpis = [
      { key: 'total',         label: 'Total',            icon: 'inventory_2',       value: lista.length, isTotal: true },
      { key: 'activo',        label: 'Disponibles',      icon: 'check_circle',      value: lista.filter(x => x.status === 'Activo').length,        tone: 'ok' },
      { key: 'mantenimiento', label: 'En mantenimiento', icon: 'build',             value: lista.filter(x => x.status === 'Mantenimiento').length, tone: 'warn' },
      { key: 'baja',          label: 'Baja',             icon: 'do_not_disturb_on', value: lista.filter(x => x.status === 'Baja').length,          tone: 'danger' },
    ];
  }

  onKpiToggle(key: string | null): void {
    this.activeKpi = key;
    this.applyKpiFilter();
  }

  onViewModeChange(mode: 'table' | 'cards'): void {
    this.viewMode = mode;
    InventoryToolbarComponent.storeView('instrumentals', mode);
  }

  // ─── Selección con checkbox (como en los catálogos) ─────────────────
  readonly selection = new SelectionModel<any>(true, [], true, (a, b) => a?.id != null && a.id === b?.id);
  private get visibleRows(): any[] { return (this.dataSource?.filteredData ?? []) as any[]; }
  isAllSelected(): boolean {
    const rows = this.visibleRows;
    return rows.length > 0 && rows.every(r => this.selection.isSelected(r));
  }
  isPartiallySelected(): boolean { return this.selection.hasValue() && !this.isAllSelected(); }
  toggleAllRows(): void {
    if (this.isAllSelected()) this.selection.deselect(...this.visibleRows);
    else this.selection.select(...this.visibleRows);
  }
  openImage(url: string): void { if (url) window.open(url, '_blank'); }

  /** Exporta a Excel el instrumental seleccionado. */
  exportSelection(): void {
    const rows = this.selection.selected;
    if (!rows.length) return;
    this.simpleExcel.simpleXLSX({
      fileName: 'Inventario de instrumental (selección)',
      sheets: [{
        sheetName: 'Selección', header: [], lineBreak: 0, data: rows,
        columns: [
          { columnKey: '_code', columnName: 'Instrumental' },
          { columnKey: '_description', columnName: 'Descripción' },
          { columnKey: 'serialNumber', columnName: 'Serie' },
          { columnKey: 'tagRfid', columnName: 'Etiqueta RFID' },
          { columnKey: 'manufacturerYear', columnName: 'Año' },
          { columnKey: 'numberUses', columnName: 'Usos' },
          { columnKey: 'type', columnName: 'Tipo' },
          { columnKey: 'status', columnName: 'Estado' },
        ],
      }],
    } as any);
  }

  private applyKpiFilter(): void {
    const lista = this.allStock ?? [];
    let filtrada = lista;
    switch (this.activeKpi) {
      case 'activo':        filtrada = lista.filter(x => x.status === 'Activo'); break;
      case 'mantenimiento': filtrada = lista.filter(x => x.status === 'Mantenimiento'); break;
      case 'baja':          filtrada = lista.filter(x => x.status === 'Baja'); break;
      default:              filtrada = lista;
    }
    this.dataSource = new MatTableDataSource(filtrada);
    this.dataSource.paginator = this.paginator;
  }

  /** Filtro de texto de la tabla. */
  applyTextFilter(event: Event): void {
    const value = (event.target as HTMLInputElement).value ?? '';
    this.dataSource.filter = value.trim().toLowerCase();
  }
}
